'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { FloatingContactFabConfig } from '@/entities'

const SESSION_STORAGE_KEY = 'assets-infinity-floating-contact-fab-dismissed'
const DEFAULT_SIZE = 72

interface FloatingContactFabProps {
  config?: FloatingContactFabConfig
}

export default function FloatingContactFab({
  config,
}: FloatingContactFabProps) {
  const [isDismissed, setIsDismissed] = useState(true)

  const enabled = config?.enabled !== false
  const size = config?.size || DEFAULT_SIZE
  const label = config?.label?.trim() || 'Contact'
  const backgroundImage = config?.backgroundImage?.trim()

  useEffect(() => {
    setIsDismissed(sessionStorage.getItem(SESSION_STORAGE_KEY) === '1')
  }, [])

  const dismissFab = () => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, '1')
    setIsDismissed(true)
  }

  if (!enabled || isDismissed) return null

  return (
    <div
      className="fixed bottom-5 right-5 z-[60] md:bottom-8 md:right-8"
      style={{ width: size, height: size }}
    >
      <Link
        href="/contact"
        aria-label={label}
        className="group relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-base-100 text-center text-sm font-semibold leading-tight text-primary-content shadow-xl ring-1 ring-black/10 transition duration-200 hover:-translate-y-0.5 hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-primary/30"
      >
        {backgroundImage ? (
          <>
            <Image
              src={backgroundImage}
              alt=""
              fill
              sizes={`${size}px`}
              className="object-cover transition duration-200 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-black/10" />
          </>
        ) : (
          <span className="px-2 text-[13px]">{label}</span>
        )}
      </Link>

      <button
        type="button"
        onClick={dismissFab}
        aria-label="Close contact shortcut"
        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-base-100 text-base-content shadow-lg ring-1 ring-base-content/15 transition hover:bg-base-200 hover:text-error focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}
